import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../../redux/store";
import {
  clearDeleteIssueState,
  clearNewIssueState,
  clearUpdateIssueState,
  deleteIssueStateAction,
  getCreateIssueState,
  getDeleteIssueState,
  getIssuesState,
  getUpdateIssueState,
  IssueResponse,
  issuesStateAction,
} from "../../../../redux/features/other-slice";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../../redux/features/common-slice";
import paths from "../../../../navigation/paths";
import PermissionGate from "../../../../core/layouts/permission-gate";
import { UserRole } from "../../../../redux/features/user-slice";
import Table from "../../../../components/table";
import Loading from "../../../../components/loading";
import TableBodyCell from "../../../../components/table-body-cell";
import Button from "../../../../components/button";
import ToastComponent from "../../../../components/toast";
import { adminCat, mgTeam } from "../../../../core/utils/constants";
import EmptyDataOverlay from "../../../../components/empty-data-overlay";
import PropertyActions from "../_components/property-actions";
import NewIssueDialog from "./components/new-issue-dialog";
import UpdateIssueDialog from "./components/update-issue-dialog";

export default function IssuesPage() {
  const navigate = useNavigate();

  const { id, name } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const issuesState = useSelector(getIssuesState);
  const newIssueState = useSelector(getCreateIssueState);
  const updateIssueState = useSelector(getUpdateIssueState);
  const deleteIssueState = useSelector(getDeleteIssueState);
  const errorState = useSelector(getErrorState);

  const [openNew, setOpenNew] = useState<boolean>(false);
  const [updateIssue, setUpdateIssue] = useState<IssueResponse | null>(null);
  const [isHover, setIsHover] = useState<{
    [id: string]: boolean;
  }>({});

  const fetchIssues = useCallback(async () => {
    if (id) {
      await dispatch(issuesStateAction({ categoryId: id as string }));
    }
  }, [dispatch, id]);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  // Memoized onMouseEnter handler
  const handleMouseEnter = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: true,
    }));
  }, []);

  // Memoized onMouseLeave handler
  const handleMouseLeave = useCallback((id: string) => {
    setIsHover((prevState) => ({
      ...prevState,
      [id]: false,
    }));
  }, []);

  const deleteIssue = async (id: string) => {
    await dispatch(deleteIssueStateAction({ id: id }));
  };

  useEffect(() => {
    fetchIssues();
    dispatch(
      setBreadcrumbState([
        { id: "summaryProperties", path: null, title: "Summary Properties" },
        { id: paths.ISSUES, path: null, title: "Issues" },
        { id: name as string, path: null, title: name as string },
      ])
    );
  }, [fetchIssues]);

  useEffect(() => {
    if (newIssueState.data && newIssueState.data.success) {
      handleSuccessToast(newIssueState.data.message, clearNewIssueState);
      fetchIssues();
    }
  }, [newIssueState, handleSuccessToast]);

  useEffect(() => {
    if (updateIssueState.data && updateIssueState.data.success) {
      handleSuccessToast(updateIssueState.data.message, clearUpdateIssueState);
    }
  }, [updateIssueState, handleSuccessToast]);

  useEffect(() => {
    if (deleteIssueState.data && deleteIssueState.data.success) {
      handleSuccessToast(deleteIssueState.data.message, clearDeleteIssueState);
    }
  }, [deleteIssueState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Supervisor]}
    >
      <Helmet title="Issues" />
      <div className="flex items-center justify-end mb-4">
        <Button
          label="Add Issue"
          onClick={() => setOpenNew(true)}
          exClassName="truncate"
        />
      </div>
      <Table
        style={{
          maxHeight: "calc(100% - 48px)",
        }}
        headerCells={[
          { id: "title", label: "Title", align: "text-left", width: "w-5/12" },
          {
            id: "description",
            label: "Description",
            align: "text-left",
            width: "w-7/12",
          },
          // {
          //   id: "group",
          //   label: "Group",
          //   align: "text-left",
          //   width: "w-2/12",
          // },
        ]}
        body={
          issuesState.loading
            ? [
                <tr key="loading-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <Loading />
                    </div>
                  </td>
                </tr>,
              ]
            : issuesState.data.length > 0
            ? [
                ...issuesState.data.map((iss: IssueResponse, index: number) => {
                  return (
                    <tr
                      key={iss.id}
                      className="border-t border-dashed border-divider hover:bg-action-hover relative"
                      onMouseEnter={() => handleMouseEnter(iss.id)}
                      onMouseLeave={() => handleMouseLeave(iss.id)}
                    >
                      <TableBodyCell exClassName="font-medium whitespace-normal break-words">
                        <label className="cursor-pointer hover:underline">
                          {iss.title}
                        </label>
                      </TableBodyCell>
                      <TableBodyCell exClassName="whitespace-normal break-words text-justify">
                        {iss.description}
                      </TableBodyCell>
                      {/* <TableBodyCell exClassName="w-2/12 whitespace-normal break-words text-justify">
                        {iss.position?.name || "All"}
                      </TableBodyCell> */}
                      {isHover[iss.id] && (
                        <PropertyActions
                          id={iss.id}
                          onUpdate={() => setUpdateIssue(iss)}
                          onDelete={() => deleteIssue(iss.id)}
                        />
                      )}
                    </tr>
                  );
                }),
              ]
            : [
                <tr key="empty-row">
                  <td colSpan={2} className="h-64">
                    <div className="flex flex-col items-center justify-center">
                      <EmptyDataOverlay
                        label="No Issues found"
                        exClassName="h-20"
                      />
                    </div>
                  </td>
                </tr>,
              ]
        }
      />
      <ToastComponent id="issues" />
      <NewIssueDialog
        categoryId={id as string}
        open={openNew}
        setOpen={setOpenNew}
        onCompleted={fetchIssues}
      />
      <UpdateIssueDialog
        issue={updateIssue}
        setIssue={setUpdateIssue}
        onCompleted={fetchIssues}
      />
    </PermissionGate>
  );
}
